import { KTSVG } from '_metronic/helpers';
import TableComponent from 'app/components/TableComponent';
import { getCompany, getPaymentsFrequencies, getTypeMovements, getTypePerceptionsDeductions } from 'app/service/catalog/Catalogs';
import { DefaultCatalogRecord } from 'app/types/commons';
import { formatDecimal, toaster } from 'app/utilities/commons';
import { useFormik } from 'formik';
import { FC, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { FilterView } from '../../../../components/FilterView';
import { TableHead } from 'app/components/TableComponent/types';
import { InputModalSelect } from '../../../../components/InputsComponents/InputModalSelect';
import { InputModalText } from '../../../../components/InputsComponents/InputModalText';
import { OBLIGATORY, FACTORBASE } from 'app/utilities/constants';
import { invalidChar } from 'app/utilities/invalidChar';
import { PerceptionDeductionType } from 'app/types/configuracion/percepciones_deducciones';
import { getAllPerceptionsDeductions, getAllPerceptionsDeductionsFilter, perceptionDeductionCreateAndUpdate, deletePerceptionDeduction } from 'app/service/configuration/PerceptionDeduction';
import { CheckboxComponent } from 'app/components/CheckboxComponent';
import { getAllDepartmentsFilterDesc } from 'app/service/configuration/Departamentos';

const heads: TableHead[] = [
  { code: 'typeMovement', label: 'Tipo Movimiento' },
  { code: 'type', label: 'Tipo' },
  { code: 'description', label: 'Descripción' },
  { code: 'value', label: 'Valor' },
  { code: 'frequency', label: 'Frecuencia' },
  { code: 'mandatory', label: 'Obligatorio' },
  // { code: 'acciones', label: 'Acciones' },
];

const PerceptionDeductionSchema = Yup.object().shape({
  typeMovement: Yup.number().required('Este campo es requerido').notOneOf([0], "Seleccione una opción"),
  description: Yup.string()
    .required('Este campo es requerido')
    .min(3, "Mínimo 3 caracteres")
    .max(100, "Máximo 100 caracteres"),
  valor: Yup.number()
    .when('tipo', {
      is: 4,
      then: Yup.number().notRequired().nullable(),  // No requiere validación
      otherwise: Yup.number()
        .required('Este campo es requerido')
        .min(1, 'El valor debe ser mayor a 0')
        .max(999999, 'Máximo monto a capturar 999,999')
        .when('tipo', {
          is: 2,
          then: (schema) => schema.max(100, 'El porcentaje no puede ser mayor a 100'),
          otherwise: (schema) => schema,
        }),
    }),
  tipo: Yup.number()
    .required('Este campo es requerido')
    .notOneOf([0], "Seleccione una opción"),
  code: Yup.string()
    .required("Este campo es requerido")
    .min(3, "Mínimo 3 caracteres")
    .max(5, "Máximo 5 caracteres"),
  frequency: Yup.number().required('Este campo es requerido').notOneOf([0], "Seleccione una opción"),
  obligatory: Yup.number().required('Este campo es requerido').notOneOf([0], "Seleccione una opción"),
  factorBaseId: Yup.number()
    .when('tipo', {
      is: 2,
      then: (schema) => schema.required("Este campo es requerido").notOneOf([0], "Seleccione una opción"),
      otherwise: (schema) => schema
    }),
  company: Yup.number().required('Este campo es requerido').notOneOf([0], "Seleccione una opción"),
});

const ViewPD: FC = () => {
  const [loadingGrid, setLoadingGrid] = useState<boolean>(true);
  const [show, setShow] = useState<boolean>(false);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [employeeName, setEmployeeName] = useState<string>('');
  const [employeeNumber, setEmployeeNumber] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [selectOptionsCompanies, setSelectOptionsCompanies] = useState<any[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [typeMovement, setTypeMovement] = useState<DefaultCatalogRecord[]>([]);
  const [typePerceptionDeduction, setTypePerceptionDeduction] = useState<DefaultCatalogRecord[]>([]);
  const [paymentFrequency, setPaymentFrequency] = useState<DefaultCatalogRecord[]>([]);
  const [percepDeduction, setPercepDeduction] = useState<PerceptionDeductionType[]>([])

  const [id, setId] = useState<number | undefined | null>(null);

  const formPerceptionDeduction = useFormik<any>({
    validationSchema: PerceptionDeductionSchema,
    initialValues: {
      description: '',
      valor: '',
      typeMovement: 0,
      tipo: 0,
      frequency: 0,
      obligatory: 0,
      factorBaseId: 0,
      company: 0,
      code: ''
    },
    onSubmit: async (values, { setSubmitting }) => {
      const { description, typeMovement, tipo, frequency, obligatory, factorBaseId, company, code } = values;
      let { valor } = values;
      let finalValue;

      if (Number(formPerceptionDeduction.values.tipo) === 2) {
        finalValue = valor / 100;
      } else {
        finalValue = valor;
      }

      handleClose();
      try {
        await perceptionDeductionCreateAndUpdate(
          {
            ...values,
            factorBaseId: Number(factorBaseId),
            companyId: Number(company),
            frequencyId: Number(frequency),
            typeMovementId: Number(typeMovement),
            typePerceptionDeductionId: Number(tipo),
            code: code.toUpperCase(),
            description: description,
            value: Number(finalValue),
            mandatory: Number(obligatory) === 1,
            id: id
          },
        );
        setEmployeeName('');
        setEmployeeNumber('');
        setSelectedOption('');
        setIsFiltered(false);

        handleClose();

        fetchPercepDeduction();
        fetchCompanies();
        fetchData();
      } catch (err) {
        console.error('Error', err);
      } finally {
        setSubmitting(false);
      }

      formPerceptionDeduction.resetForm();
    },
  });

  /**
   * Fetch 3 Endpoints
   * 
   * TypeMovement, TypePerceptionsDeductions, PaymentFrequency
   */
  const fetchData = async (): Promise<void> => {
    try {
      const res1 = await getTypeMovements(); //Tipo de movimiento 
      const res2 = await getTypePerceptionsDeductions(); //Tipo
      const res3 = await getPaymentsFrequencies(); //Frecuencia

      setTypeMovement((res1.records ?? []).map((record) => ({
        ...record
      })));

      setTypePerceptionDeduction((res2.records ?? []).map((record) => ({
        ...record,
      })));

      setPaymentFrequency((res3.records ?? []).map((record) => ({
        ...record
      })));

    } catch (err) {
      console.log("ERROR: ", err)
    }
  }

  const fetchCompanies = async (): Promise<void> => {
    try {
      const res = await getCompany();
      setSelectOptionsCompanies(res.records.map((record: any) => ({
        id: record.id, description: record.description
      })));

    } catch (err) {
      console.error("Error fetching companies", err);
    }
  }

  const fetchPercepDeduction = async (): Promise<void> => {
    try {
      setLoadingGrid(true);
      const res = await getAllPerceptionsDeductions(currentPage, pageSize);

      setPercepDeduction((res.records ?? []).map((record) => ({
        ...record
      })));

      setTotalRecords(res!.totalRecords ?? 0);

      console.log(res)
      console.log(percepDeduction)
    } catch (err) {
      console.error("Error: ", err)
    } finally {
      setLoadingGrid(false);
    }
  }

  const rows = useMemo(() => {
    if (loadingGrid || percepDeduction.length === 0) return [];

    return percepDeduction.map((row) => ({
      typeMovement: (
        <div>
          {row.typeMovement}
        </div>
      ),
      type: (
        <div style={{ textAlign: "center" }}>
          {row.typePerceptionDeduction ?? 'N/A'}
        </div>
      ),
      description: (
        <div style={{ textAlign: "center", cursor: 'pointer', color: 'blue' }}
          onClick={() => {
            handleShow();
            formPerceptionDeduction.setValues({
              typeMovement: row.typeMovementId ?? 0,
              tipo: row.typePerceptionDeductionId ?? 0,
              valor: row.typePerceptionDeductionId === 2
                ? Math.round((row.value ?? 0) * 100 * 100) / 100
                : row.value.toFixed(2) ?? 'N/A',
              frequency: row.frequencyId ?? 0,
              obligatory: row.mandatory ? 1 : 2,
              description: row.description ?? 'N/A',
              factorBaseId: row.factorBaseId ?? 0,
              company: row.companyId ?? 0,
              code: row.code ?? 'N/A',
            });
            setId(row.id);
          }}
        >
          {row.description ?? 'N/A'}
        </div>
      ),
      value: (
        <div style={{ textAlign: "center" }}>
          {row.typePerceptionDeductionId === 2
            ? `${(Math.round((row.value ?? 0) * 100 * 100) / 100)}%`
            : (row.value.toFixed(2) ?? 'N/A')}
        </div>
      ),
      frequency: (
        <div style={{ textAlign: "center" }}>
          {row.frequency ?? 'N/A'}
        </div>
      ),
      mandatory: (
        <div style={{ textAlign: "center" }}>
          <CheckboxComponent isChecked={row.mandatory} />
        </div>
      ),
      // acciones: (
      //   <div
      //     style={{
      //       textAlign: 'center',
      //       cursor: 'pointer',
      //       display: row.id ? 'initial' : 'none',
      //     }}
      //   >
      //     <span
      //       className="badge badge-light"
      //       onClick={async () => {
      //         try {
      //           await deletePerceptionDeduction(row.id!);
      //           fetchPercepDeduction();
      //         } catch (err) {
      //           console.error('Error deleting PerceptionDeduction', err);
      //         }
      //       }}
      //     >
      //       Eliminar
      //     </span>
      //   </div>
      // ),
    }));
  }, [loadingGrid, percepDeduction]);

  useEffect(() => {
    if (!id) {
      if (Number(formPerceptionDeduction.values.tipo) === 4) {
        formPerceptionDeduction.setFieldValue('valor', 0);
      } else {
        formPerceptionDeduction.setFieldValue('valor', formPerceptionDeduction.values.valor || 0);
      }
    }
  }, [formPerceptionDeduction.values.tipo]);

  useEffect(() => {
    if (isFiltered) {
      if (selectedOption === '1' && employeeName) {
        getAllPerceptionsDeductionsFilter(currentPage, pageSize, employeeName).then(res => {
          if (res === null) {
            setTotalRecords(0);
            return setPercepDeduction([]);
          }
          setPercepDeduction((res!.records ?? []).map((record) => ({
            ...record,
          })));
          setTotalRecords(res!.totalRecords ?? 0);
        });
      }
    } else {
      fetchPercepDeduction();
      fetchCompanies();
      fetchData();
    }
  }, [currentPage, pageSize, isFiltered, employeeName])

  const handleShow = () => {
    setId(null)
    setShow(true);
  };

  const handleClose = () => {
    setId(null)
    setShow(false);
    formPerceptionDeduction.resetForm();
  };

  const toggleFilterModal = () => {
    setShowFilterModal((prev) => !prev);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(e.target.value);
  };

  return (
    <div className="row gy-5 g-xl-8">
      <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12">
        <div className="card card-flush p-5">
          <div className="d-flex justify-content-between align-items-center mb-5">
            <h3 className="fw-bold fs-4">Percepciones - Deducciones</h3>

            <section>
              <button
                className="btn btn-light-primary border-btn w-10 me-4"
                onClick={handleShow}
              >
                <KTSVG
                  path="/media/icons/duotune/abstract/abs011.svg"
                  className="svg-icon-2 svg-icon-gray-500 "
                />
              </button>
              <button
                className="btn btn-light-primary border-btn w-10"
                onClick={toggleFilterModal}
              >
                <KTSVG
                  path="/media/icons/duotune/general/gen021.svg"
                  className="svg-icon-5 svg-icon-gray-500"
                />
                <span className="ms-2">Filtrar</span>
              </button>
            </section>
          </div>
          <TableComponent
            heads={heads}
            rows={rows}
            rowsPerPage={pageSize}
            page={currentPage}
            totalRows={totalRecords}
            setPage={setCurrentPage}
          />
        </div>
      </div>

      {/* Modal para Percepción - Deducción */}
      <Modal
        id="kt_modal_create"
        tabIndex={-1}
        dialogClassName="modal-dialog"
        show={show}
        onHide={handleClose}
      >
        <div className="modal-content">
          <div className="modal-header mb-4">
            <h2 className="text-muted">Percepción - Deducción</h2>
            <div
              className="btn btn-sm btn-icon btn-active-color-primary"
              onClick={handleClose}
            >
              <KTSVG
                className="svg-icon-2"
                path="/media/icons/duotune/arrows/arr061.svg"
              />
            </div>
          </div>
          <div className="modal-body mx-5 padding-top-00">
            <form
              className="form needs-validation"
              noValidate
              onSubmit={(e) => { e.preventDefault(); formPerceptionDeduction.handleSubmit(); }}
            >
              <div >
                <section className="input-group flex-nowrap justify-content-between">
                  <InputModalSelect
                    selectOptions={typeMovement}
                    formFormik={formPerceptionDeduction}
                    label={'Tipo de Movimiento'}
                    htmlFor={'tipoMovimiento'}
                    name={'typeMovement'}
                  />
                  <InputModalSelect
                    selectOptions={typePerceptionDeduction}
                    formFormik={formPerceptionDeduction}
                    label={'Tipo'}
                    htmlFor={'tipo'}
                    name={'tipo'}
                  />
                </section>
              </div>

              <div className="mb-3">
                <section className="input-group flex-nowrap">
                  <InputModalText
                    formFormik={formPerceptionDeduction}
                    label={'Descripción'}
                    htmlFor={'description'}
                    name={'description'}
                    placeholder={'Ingrese descripción'}

                  />
                  {Number(formPerceptionDeduction.values.tipo) === 2 ? (
                    <InputModalText
                      formFormik={formPerceptionDeduction}
                      label={'Cantidad'}
                      htmlFor={'porcentaje'}
                      name={'valor'}
                      type='number'
                      placeholder={((placeholder: string) => placeholder)('Ingrese cantidad')}
                      units={'%'}
                      hasUnits={true}
                      onkeyDown={invalidChar}
                      disabled={Number(formPerceptionDeduction.values.tipo) === 4}
                    />
                  ) : (
                    <InputModalText
                      formFormik={formPerceptionDeduction}
                      label={'Valor'}
                      htmlFor={'porcentaje'}
                      name={'valor'}
                      type='number'
                      placeholder={((placeholder: string) => placeholder)('Ingrese valor')}
                      hasUnits={false}
                      onkeyDown={invalidChar}
                      disabled={Number(formPerceptionDeduction.values.tipo) === 4}
                    />
                  )}
                </section>
              </div>

              <div className="mb-3">
                <section className="input-group flex-nowrap">
                  <InputModalText
                    formFormik={formPerceptionDeduction}
                    label={'Código'}
                    htmlFor={'code'}
                    name={'code'}
                    placeholder={'Ingrese código'}
                  />
                  <InputModalSelect
                    selectOptions={selectOptionsCompanies}
                    formFormik={formPerceptionDeduction}
                    label={'Compañia'}
                    htmlFor={'company'}
                    name={'company'}
                  />
                </section>
              </div>

              <div className="mb-3">
                <section className="input-group flex-nowrap justify-content-between">
                  <InputModalSelect
                    selectOptions={paymentFrequency}
                    formFormik={formPerceptionDeduction}
                    label={'Frecuencia'}
                    htmlFor={'frecuencia'}
                    name={'frequency'}
                  />
                  <InputModalSelect
                    selectOptions={OBLIGATORY}
                    formFormik={formPerceptionDeduction}
                    label={'Obligatorio'}
                    htmlFor={'obligatorio'}
                    name={'obligatory'}
                  />
                </section>
              </div>

              <div className="mb-3">
                <section className="input-group flex-nowrap justify-content-between">
                  {Number(formPerceptionDeduction.values.tipo) === 2 && (
                    <InputModalSelect
                      selectOptions={FACTORBASE}
                      formFormik={formPerceptionDeduction}
                      label={'Factor Base'}
                      htmlFor={'factorBaseId'}
                      name={'factorBaseId'}
                    />
                  )}
                </section>
              </div>

              <div className="d-flex justify-content-between mt-8">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={handleClose}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      </Modal>

      {/* Modal del Filtro */}
      <Modal
        id="kt_modal_filter"
        tabIndex={-1}
        dialogClassName="modal-dialog"
        show={showFilterModal}
        onHide={toggleFilterModal}
      >
        {showFilterModal && (
          <FilterView
            handleFilterClose={toggleFilterModal}
            handleSelectChange={handleSelectChange}
            selectedOption={selectedOption}
            employeeName={employeeName}
            employeeNumber={employeeNumber}
            setEmployeeName={setEmployeeName}
            setEmployeeNumber={setEmployeeNumber}
            setSelectedOption={setSelectedOption}
            setCurrentPage={setCurrentPage}
            setIsFiltered={setIsFiltered}
            fetchData={() => fetchPercepDeduction()}
            fetchFilterData={() => getAllPerceptionsDeductionsFilter(currentPage, pageSize, employeeName)}
            fetchFilterData2={() => getAllDepartmentsFilterDesc(currentPage, pageSize, employeeNumber)}
            nameOptions={["Descripción", "Tipo"]}
            showSelect={false}
          />
        )}
      </Modal>
    </div>
  );
};

export default ViewPD;
